import { useEffect, useState } from "react";

const CONSENT_KEY = "is-cookie-consent-v1";
const MEASUREMENT_ID = "G-KC9969RM4W";

const DENIED = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

const updateConsent = (analyticsGranted) => {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ...DENIED,
      analytics_storage: analyticsGranted ? "granted" : "denied",
    });
  }
};

const sendPageView = () => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      send_to: MEASUREMENT_ID,
    });
  }
};

const readConsent = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (saved) {
      updateConsent(saved.analytics === true);
      if (saved.analytics === true) sendPageView();
    } else {
      setShowBanner(true);
    }
    const openSettings = () => {
      const current = readConsent();
      setAnalyticsOn(current?.analytics === true);
      setShowBanner(false);
      setShowPrefs(true);
    };
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, []);

  const save = (analytics) => {
    updateConsent(analytics);
    if (analytics) sendPageView();
    try {
      window.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ necessary: true, analytics, ts: new Date().toISOString() })
      );
    } catch {}
    setShowBanner(false);
    setShowPrefs(false);
  };

  if (!showBanner && !showPrefs) return null;

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
          className="fixed inset-x-4 bottom-4 z-50 rounded-md border border-[var(--line)] bg-[var(--cream)] p-6 shadow-lg md:left-auto md:right-6 md:max-w-md"
        >
          <p className="font-display text-lg">Cookies on this website</p>
          <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
            We use necessary technologies to run this site and, with your
            permission, privacy-conscious analytics (Google Analytics) to
            understand general usage. Analytics is off unless you accept.{" "}
            <a href="/privacy" data-testid="cookie-banner-privacy-link" className="underline decoration-[var(--line)] underline-offset-2 hover:text-[var(--ink)]">
              Privacy Policy
            </a>
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="cookie-accept-all-btn"
              onClick={() => save(true)}
              className="btn-primary !px-5 !py-2.5 !text-sm"
            >
              Accept All
            </button>
            <button
              type="button"
              data-testid="cookie-reject-all-btn"
              onClick={() => save(false)}
              className="btn-outline !px-5 !py-2.5 !text-sm"
            >
              Reject All
            </button>
            <button
              type="button"
              data-testid="cookie-preferences-btn"
              onClick={() => {
                setShowBanner(false);
                setShowPrefs(true);
              }}
              className="text-sm text-[var(--ink-soft)] underline decoration-[var(--line)] underline-offset-4 transition-colors duration-200 hover:text-[var(--ink)]"
            >
              Preferences
            </button>
          </div>
        </div>
      )}

      {showPrefs && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          data-testid="cookie-preferences-modal"
          className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--ink)]/40 p-4 sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrefs(false);
          }}
        >
          <div className="w-full max-w-md rounded-md border border-[var(--line)] bg-[var(--cream)] p-7 shadow-xl">
            <p className="font-display text-xl">Cookie preferences</p>

            <div className="mt-6 flex items-start justify-between gap-4 border-t border-[var(--line)] pt-5">
              <div>
                <p className="text-sm font-medium">Necessary</p>
                <p className="mt-1 text-sm leading-relaxed text-[var(--ink-soft)]">
                  Required for the website to function. Always active.
                </p>
              </div>
              <span
                data-testid="cookie-necessary-state"
                className="shrink-0 rounded-full border border-[var(--line)] px-3 py-1 text-xs text-[var(--ink-soft)]"
              >
                Always on
              </span>
            </div>

            <div className="mt-5 flex items-start justify-between gap-4 border-t border-[var(--line)] pt-5">
              <div>
                <p className="text-sm font-medium">Analytics</p>
                <p className="mt-1 text-sm leading-relaxed text-[var(--ink-soft)]">
                  Google Analytics, used to understand general website usage.
                  Off by default.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsOn}
                aria-label="Analytics cookies"
                data-testid="cookie-analytics-toggle"
                onClick={() => setAnalyticsOn(!analyticsOn)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${
                  analyticsOn ? "bg-[var(--rust)]" : "bg-[var(--line)]"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-[var(--cream)] shadow transition-transform duration-200 ${
                    analyticsOn ? "translate-x-[1.375rem]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid="cookie-save-preferences-btn"
                onClick={() => save(analyticsOn)}
                className="btn-primary !px-5 !py-2.5 !text-sm"
              >
                Save Preferences
              </button>
              <button
                type="button"
                data-testid="cookie-preferences-close-btn"
                onClick={() => setShowPrefs(false)}
                className="text-sm text-[var(--ink-soft)] underline decoration-[var(--line)] underline-offset-4 transition-colors duration-200 hover:text-[var(--ink)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;
